import { SdkContext } from '../internal'
import { versionedSettingsWrite } from '@/lib/realtime/settingsVersion'
import { unmodifiedSince } from '@/lib/realtime/rowVersion'

/** F-146: where a custom domain is in its setup. FAILED keeps the row until it is checked again or removed. */
export type CustomDomainStatus = 'PENDING_DNS' | 'VERIFYING' | 'CERT_ISSUED' | 'ACTIVE' | 'FAILED'

export type CustomDomainErrorCode =
  | 'dnsNotFound' | 'dnsWrongTarget' | 'dnsARecord' | 'dnsProxied' | 'dnsApex'
  | 'caaBlocked' | 'acmRateLimited' | 'acmFailing' | 'httpsNotReady' | 'providerError' | 'expired'

export interface CustomDomainRow {
  id: string
  hostname: string
  status: CustomDomainStatus
  /** A root domain (no CNAME there): the record is an ALIAS/ANAME on `@`. */
  apex: boolean
  /** The record the community creates at its DNS provider. `target` is null until the hosting answers. */
  record: { type: string; name: string; host: string; target: string | null }
  /** `message` is already translated and says what to do. */
  error: { code: CustomDomainErrorCode; message: string } | null
  /** Set on a swap: the active row this one replaces once it is active. */
  replacesId: string | null
  requestedAt: string | null
  checkedAt: string | null
  dnsVerifiedAt: string | null
  certIssuedAt: string | null
  activeAt: string | null
  failedAt: string | null
  expiresAt: string | null
}

export interface CustomDomainView {
  /** The `<slug>.memberfy.net` address. Always serves the community. */
  subdomain: string | null
  /** The address links and e-mails use: the active custom domain, else the subdomain. */
  primaryDomain: string | null
  /** The plan includes a custom domain (`branding.customDomain`). */
  entitled: boolean
  active: CustomDomainRow | null
  /** A first domain in setup, or the one replacing `active`. */
  pending: CustomDomainRow | null
}

/** `pending` gives up a swap (or a first request) and keeps the active one; `all` removes both. */
export type CustomDomainRemoveScope = 'all' | 'pending'

/**
 * F-146: the community's custom domain. Without `communityId` the routes of
 * the session's community (X-CommunityId; owner and admin); with it, the
 * SuperAdmin's pair for that community. Every call answers the whole view.
 */
export class CustomDomainResource {
  constructor(private ctx: SdkContext) {}

  private base(communityId?: string) {
    return communityId ? '/admin/communities/{communityId}/custom-domain' : '/communities/custom-domain'
  }

  async get(communityId?: string): Promise<CustomDomainView> {
    return this.ctx.call({ httpMethod: 'GET', path: this.base(communityId) }, communityId ? { communityId } : {})
  }

  /** Adds the domain, or swaps to it while the active one keeps serving. */
  /** The community's own domain sends the settings version read (the SuperAdmin's route does not version). */
  async request(hostname: string, communityId?: string): Promise<CustomDomainView> {
    const write = (v: string | null | undefined) => this.ctx.call<CustomDomainView>(
      { httpMethod: 'PUT', path: this.base(communityId), hasBody: true },
      { ...(communityId ? { communityId } : {}), body: { hostname, ...(communityId ? {} : (v ? { settingsUpdatedAt: v } : {})) } }
    )
    return communityId ? write(null) : versionedSettingsWrite(write)
  }

  async remove(scope: CustomDomainRemoveScope, communityId?: string): Promise<CustomDomainView> {
    const write = (v: string | null | undefined) => this.ctx.call<CustomDomainView>(
      { httpMethod: 'DELETE', path: this.base(communityId) },
      { ...(communityId ? { communityId } : {}), query: { scope }, ...(communityId ? {} : { headers: unmodifiedSince(v) }) }
    )
    return communityId ? write(null) : versionedSettingsWrite(write)
  }

  /** "Check now": runs one step on the pending domain right away. */
  async check(communityId?: string): Promise<CustomDomainView> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `${this.base(communityId)}/check`, hasBody: true },
      communityId ? { communityId } : {}
    )
  }
}
